import {
  BulkUploadTabs,
  SupportedDateFormatforCsvMapping,
  TransactionTemplateFormatEnum,
  TransactionTemplateImportTypeEnum,
} from '@commenda/api/sales-tax/types';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  HorizontalTabsList,
  SingleCombobox,
  Tabs,
  TabsTrigger,
} from '@commenda/kakashi';
import { Icons } from '@commenda/kakashi/src/components/Icons';
import { useEffect, useState } from 'react';
import { useColumnMappingsForUpload } from '../../common/utils/useColumnMappingForUpload';

import { getSalesTaxTransactionsColumnListForImport } from '../utils';

interface ColumnMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleBack: () => void;
  onUpload: (
    mapping: Record<string, string>,
    dateFormat: SupportedDateFormatforCsvMapping
  ) => void;
  csvColumnsFromUpload: string[];
  fileName: string;
  disabled?: boolean;
  templateFormat: TransactionTemplateFormatEnum;
  transactionImportType: TransactionTemplateImportTypeEnum;
  isTaxInclusiveTransactions: boolean;
  openTaxCalculationSettingModal: () => void;
  /** ISO 3166-1 country code for the corporation's incorporation country */
  incorporationCountry?: string;
  /** Whether to use the EU/UK upload flow (simplified flow without template selection) */
  isEuUploadFlow?: boolean;
}

const TransactionsColumnMappingModal = ({
  isOpen,
  onClose,
  handleBack,
  onUpload,
  csvColumnsFromUpload = [],
  fileName,
  disabled = false,
  templateFormat,
  transactionImportType,
  openTaxCalculationSettingModal,
  isTaxInclusiveTransactions,
  isEuUploadFlow = false,
}: ColumnMappingModalProps) => {
  // Use isEuUploadFlow prop for simplified flow without template selection
  const isDeGb = isEuUploadFlow;

  const templateColumns = getSalesTaxTransactionsColumnListForImport(
    transactionImportType,
    isEuUploadFlow
  );

  const { mappings, handleColumnSelect, saveMappings } =
    useColumnMappingsForUpload({
      entity: 'transaction',
      templateColumns,
      csvColumns: csvColumnsFromUpload,
      resetKey: isOpen,
      autoMap: true,
    });

  const TABS: {
    title: string;
    tabId: string;
  }[] = [
      { title: 'Required Columns', tabId: BulkUploadTabs.REQUIRED_COLUMNS },
      { title: 'Optional Columns', tabId: BulkUploadTabs.OPTIONAL_COLUMNS },
      { title: 'Advanced Columns', tabId: BulkUploadTabs.ADVANCED_COLUMNS },
    ];

  // For DE/GB, only show Required and Optional tabs (no Advanced)
  // For India, only show Required tab
  // For other countries, show based on template format
  const visibleTabs = (() => {
    if (isDeGb) {
      return TABS.filter(
        (tab) => tab.tabId !== BulkUploadTabs.ADVANCED_COLUMNS
      );
    }
    return templateFormat === TransactionTemplateFormatEnum.ADVANCED
      ? TABS
      : TABS.filter((tab) => tab.tabId !== BulkUploadTabs.ADVANCED_COLUMNS);
  })();

  const [currentTab, setCurrentTab] = useState(BulkUploadTabs.REQUIRED_COLUMNS);

  useEffect(() => {
    if (!isOpen) {
      setCurrentTab(BulkUploadTabs.REQUIRED_COLUMNS);
    }
  }, [isOpen]);

  const visibleMappings = mappings.filter((mapping) => {
    const matchesTab =
      (currentTab === BulkUploadTabs.REQUIRED_COLUMNS &&
        mapping.required &&
        !mapping.advanced) ||
      (currentTab === BulkUploadTabs.OPTIONAL_COLUMNS &&
        !mapping.required &&
        !mapping.advanced) ||
      (currentTab === BulkUploadTabs.ADVANCED_COLUMNS && mapping.advanced);

    // Hide Item Tax column when isTaxInclusiveTransactions is true
    if (isTaxInclusiveTransactions && mapping.tableColumn === 'Item Tax') {
      return false;
    }

    return matchesTab;
  });

  const [dateFormat, setDateFormat] =
    useState<SupportedDateFormatforCsvMapping | null>(null);

  const DATE_FORMATS: {
    value: SupportedDateFormatforCsvMapping;
    label: string;
  }[] = [
      { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
      { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
      { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
      { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY' },
      { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
      { value: 'YYYY-DD-MM', label: 'YYYY-DD-MM' },
      { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD' },
      { value: 'YYYY/DD/MM', label: 'YYYY/DD/MM' },
      { value: 'M-D-YYYY', label: 'M-D-YYYY' },
      { value: 'D-M-YYYY', label: 'D-M-YYYY' },
      { value: 'M/D/YYYY', label: 'M/D/YYYY' },
      { value: 'D/M/YYYY', label: 'D/M/YYYY' },
      { value: 'Y-D-M', label: 'Y-D-M' },
      { value: 'Y/D/M', label: 'Y/D/M' },
      { value: 'YY-MM-DD', label: 'YY-MM-DD' },
      { value: 'YY/MM/DD', label: 'YY/MM/DD' },
      { value: 'YY-DD-MM', label: 'YY-DD-MM' },
      { value: 'YY/DD/MM', label: 'YY/DD/MM' },
      { value: 'DD-MM-YY', label: 'DD-MM-YY' },
      { value: 'DD/MM/YY', label: 'DD/MM/YY' },
      { value: 'MM-DD-YY', label: 'MM-DD-YY' },
      { value: 'MM/DD/YY', label: 'MM/DD/YY' },
      { value: 'M-D-YY', label: 'M-D-YY' },
      { value: 'D-M-YY', label: 'D-M-YY' },
      { value: 'M/D/YY', label: 'M/D/YY' },
      { value: 'D/M/YY', label: 'D/M/YY' },
      { value: 'YY-D-M', label: 'YY-D-M' },
      { value: 'YY/D/M', label: 'YY/D/M' },
    ];

  const handleUpload = () => {
    const filteredMappings = mappings.filter(
      (mapping) =>
        // Only include mapping if:
        // 1. Template format is ADVANCED or mapping is not advanced AND
        // 2. Not Item Tax when tax inclusive
        (templateFormat === TransactionTemplateFormatEnum.ADVANCED ||
          !mapping.advanced) &&
        !(isTaxInclusiveTransactions && mapping.tableColumn === 'Item Tax')
    );

    const tableMappingObject = filteredMappings.reduce(
      (acc, mapping) => {
        if (mapping.csvColumn) {
          acc[mapping.tableColumn] = mapping.csvColumn;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    const labelMappingObject = filteredMappings.reduce(
      (acc, mapping) => {
        if (mapping.csvColumn) {
          acc[mapping.label] = mapping.csvColumn;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    saveMappings(labelMappingObject);
    onUpload(tableMappingObject, dateFormat);
  };

  const isValid = mappings.every(
    (mapping) =>
      !mapping.required ||
      (isTaxInclusiveTransactions && mapping.tableColumn === 'Item Tax') ||
      mapping.csvColumn !== null
  );

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="bg-white min-w-[650px] flex flex-col max-h-[90vh]"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader className="px-0 pb-0 border-b-0">
          <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col gap-1 px-6">
              <h2 className="txt-large-bold text-text-primary">
                Map CSV columns to {isDeGb ? '' : 'Indirect Tax '}transactions
                table
              </h2>
              <p className="txt-small-medium text-text-tertiary">
                <span className="txt-small-regular text-text-tertiary">
                  File:
                </span>{' '}
                {fileName}
              </p>
            </div>
            <Tabs
              value={currentTab}
              onValueChange={(value) => setCurrentTab(value as BulkUploadTabs)}
              className="h-12 w-full"
            >
              <HorizontalTabsList
                variant="underline"
                className="px-6 flex items-center gap-6 h-full"
              >
                {visibleTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.tabId}
                    value={tab.tabId}
                    className="txt-small-regular text-text-secondary data-[state=active]:txt-small-bold text-center h-full !pb-0"
                  >
                    {tab.title}
                  </TabsTrigger>
                ))}
              </HorizontalTabsList>
            </Tabs>
          </div>
        </DialogHeader>

        <div className="p-6 overflow-y-auto h-[600px]">
          {currentTab === BulkUploadTabs.OPTIONAL_COLUMNS ? (
            <Alert
              variant="info"
              description="Optional columns are not required for import, but they can improve the accuracy of Commenda's tax engine."
            />
          ) : null}

          <div className="grid grid-cols-[1.5fr,40px,2fr] gap-6 mb-3 mt-6">
            <div className="txt-small-medium text-text-tertiary">
              Template Columns
            </div>

            <div />
            <div className="txt-small-medium text-text-tertiary">
              Uploaded Columns
            </div>
          </div>

          {visibleMappings.map((mapping) =>
            mapping.tableColumn === 'Transaction Date (YYYY-MM-DD)' ? (
              <div key={mapping.label}>
                <div className="grid grid-cols-[1.5fr,40px,2fr] gap-6 items-center mb-3">
                  <div className="txt-medium-medium text-text-primary">
                    Transaction Date
                    {mapping.required && (
                      <span className="ml-0.5 txt-small-medium text-utility-violet-600">
                        *
                      </span>
                    )}
                  </div>
                  <Icons.ArrowRight className="size-5 text-fg-quaternary justify-self-center" />
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SingleCombobox
                        value={mapping.csvColumn || ''}
                        hideSearch={templateColumns.length < 8}
                        setValue={(value) =>
                          handleColumnSelect(mapping.tableColumn, value)
                        }
                        options={csvColumnsFromUpload
                          .filter(
                            (column) =>
                              column === mapping.csvColumn ||
                              !mappings.some((m) => m.csvColumn === column)
                          )
                          .map((column) => ({
                            value: column,
                            label: column,
                          }))}
                        placeholder={`Select column ${mapping.required ? ' (required)' : ''
                          }`}
                        _className="txt-medium-regular text-text-secondary h-11 px-3"
                        renderTrigger={(val) => (
                          <span className="txt-medium-medium text-text-primary">
                            {val.selectedOption?.label || val.placeholder}
                          </span>
                        )}
                      />
                    </div>
                    <div
                      className="flex items-center justify-center size-8 hover:bg-bg-secondary rounded-md"
                      onClick={() =>
                        handleColumnSelect(mapping.tableColumn, null)
                      }
                    >
                      <Icons.XClose className="size-4 text-fg-quaternary" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-[1.5fr,40px,2fr] gap-6 items-center mb-3">
                  <span />
                  <span />
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SingleCombobox
                        value={dateFormat || ''}
                        hideSearch={csvColumnsFromUpload.length === 1}
                        setValue={(val) =>
                          setDateFormat(val as SupportedDateFormatforCsvMapping)
                        }
                        options={DATE_FORMATS}
                        searchPlaceholder="Search date formats..."
                        placeholder="MM/DD/YYYY"
                        _className="txt-medium-regular text-text-secondary h-11 px-3"
                        renderTrigger={(val) => (
                          <span className="txt-medium-medium text-text-primary">
                            {val.selectedOption?.label || val.placeholder}
                          </span>
                        )}
                      />
                    </div>
                    <span className="size-8" />
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="grid grid-cols-[1.5fr,40px,2fr] gap-6 items-center mb-3"
                key={mapping.tableColumn}
              >
                <div className="txt-medium-medium text-text-primary">
                  {mapping.label}
                  {mapping.required && (
                    <span className="ml-0.5 txt-small-medium text-utility-violet-600">
                      *
                    </span>
                  )}
                </div>
                <Icons.ArrowRight className="size-5 text-fg-quaternary justify-self-center" />
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <SingleCombobox
                      value={mapping.csvColumn || ''}
                      hideSearch={templateColumns.length < 8}
                      setValue={(value) =>
                        handleColumnSelect(mapping.tableColumn, value)
                      }
                      options={csvColumnsFromUpload
                        .filter(
                          (column) =>
                            column === mapping.csvColumn ||
                            !mappings.some((m) => m.csvColumn === column)
                        )
                        .map((column) => ({
                          value: column,
                          label: column,
                        }))}
                      placeholder={`Select column ${mapping.required ? ' (required)' : ''
                        }`}
                      _className="txt-medium-regular text-text-secondary h-11 px-3"
                      renderTrigger={(val) => (
                        <span className="txt-medium-medium text-text-primary">
                          {val.selectedOption?.label || val.placeholder}
                        </span>
                      )}
                    />
                  </div>
                  <div
                    className="flex items-center justify-center size-8 hover:bg-bg-secondary rounded-md"
                    onClick={() =>
                      handleColumnSelect(mapping.tableColumn, null)
                    }
                  >
                    <Icons.XClose className="size-4 text-fg-quaternary" />
                  </div>
                </div>
              </div>
            )
          )}

          {currentTab === BulkUploadTabs.REQUIRED_COLUMNS && !isDeGb && (
            <div className="txt-small-regular text-text-tertiary">
              {!isTaxInclusiveTransactions ? (
                <>
                  Calculation is set to{' '}
                  <span
                    className="txt-small-bold text-utility-blue-600 cursor-pointer"
                    onClick={openTaxCalculationSettingModal}
                  >
                    tax exclusive
                  </span>{' '}
                  (recommended). Click to change.
                </>
              ) : (
                <>
                  Calculation is set to{' '}
                  <span
                    className="txt-small-bold text-utility-blue-600 cursor-pointer"
                    onClick={openTaxCalculationSettingModal}
                  >
                    tax inclusive
                  </span>
                  . Click to change.
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <Button
              variant="tertiary"
              onClick={handleBack}
              size="sm"
              type="button"
            >
              <Icons.ArrowLeft className="size-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={onClose} size="sm">
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!isValid || disabled || !dateFormat}
                size="sm"
              >
                Upload
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionsColumnMappingModal;
